import type { Moment } from 'moment';
import * as React from 'react';

import { noteSessionRefusal } from '../auth/signOutReason';
import config from '../config';
import { ContextStore } from '../store/contextStore';
import type {
    OnboardingDiscoveryRequest,
    OnboardingDiscoveryStarted,
    OnboardingFinishResult,
    OnboardingGoLive,
    OnboardingLogo,
    OnboardingLogoChoice,
    OnboardingSelectionRequest,
    OnboardingSelectionResult,
    OnboardingSetupStatus,
    OnboardingState,
} from './types';

// The Onboarding page's calls (contract: apiv2/onboarding.openapi.yaml, keyed by operationId so
// onboardingContract.test.js can hold every path and method to the contract).
//
// Plain fetch, not the data provider, as the Developer section does (developer/api.ts): the
// provider signs the session out on any 403, while here `403 permission_denied` only means this
// user may read but not change the onboarding, and `409 not_easy` means the page is not offered --
// answers the page shows. It also keeps the franchise wrapper from adding `franchise_to` /
// `tour_operator` to these own-tenant calls, and streams the logo candidates as blobs. A 401 (an
// expired session) still signs out.
export const ENDPOINTS = {
    onboardingState: { method: 'GET', path: '/v2/edit/onboarding/state/' },
    onboardingLogo: { method: 'GET', path: '/v2/edit/onboarding/logo/' },
    onboardingSetLogo: { method: 'POST', path: '/v2/edit/onboarding/logo/' },
    onboardingLogoCandidateImage: { method: 'GET', path: '/v2/edit/onboarding/logo/candidates/{index}/image/' },
    onboardingDiscovery: { method: 'POST', path: '/v2/edit/onboarding/discovery/' },
    onboardingSelection: { method: 'POST', path: '/v2/edit/onboarding/selection/' },
    onboardingFinish: { method: 'POST', path: '/v2/edit/onboarding/finish/' },
    onboardingRequestGoLive: { method: 'POST', path: '/v2/edit/onboarding/go_live_request/' },
    onboardingWithdrawGoLiveRequest: { method: 'POST', path: '/v2/edit/onboarding/go_live_request/withdraw/' },
    // The setup gate's (SetupGate.tsx): any signed-in user, never a 403.
    onboardingSetupStatus: { method: 'GET', path: '/v2/edit/onboarding/setup_status/' },
} as const;

export type OperationId = keyof typeof ENDPOINTS;

const EDIT_PREFIX = '/v2/edit';

/** A refused or failed call. `code` is the contract's stable code when the body has one;
 *  `fieldErrors` are DRF's `{<field>: [<message>, …]}` of a 400 the endpoint could not read. */
export class OnboardingApiError extends Error {
    status: number;
    code: string | null;
    body: any;
    fieldErrors: Record<string, string[]>;
    retryAfter: number | null;

    constructor(status: number, body: any, retryAfter: number | null = null) {
        super(errorMessage(status, body));
        this.name = 'OnboardingApiError';
        this.status = status;
        this.body = body;
        this.code = (body && typeof body.code === 'string' && body.code) || null;
        this.fieldErrors = fieldErrorsOf(body);
        this.retryAfter = retryAfter;
    }
}

const fieldErrorsOf = (body: any): Record<string, string[]> => {
    const out: Record<string, string[]> = {};
    if (!body || typeof body !== 'object' || Array.isArray(body)) {
        return out;
    }
    Object.entries(body).forEach(([field, value]) => {
        if (Array.isArray(value)) {
            out[field] = value.map(String);
        }
    });
    return out;
};

function errorMessage(status: number, body: any): string {
    if (body && typeof body.detail === 'string' && body.detail) {
        return body.detail;
    }
    const fields = Object.values(fieldErrorsOf(body)).flat();
    if (fields.length > 0) {
        return fields.join(' ');
    }
    if (status === 0) {
        return 'The server could not be reached. Check your connection and try again.';
    }
    if (status === 403) {
        return 'You do not have permission to do this.';
    }
    if (status === 404) {
        return 'Not found.';
    }
    if (status >= 500) {
        return 'The server could not answer. Try again in a moment.';
    }
    return `The request was refused (${status}).`;
}

/** A refusal that a retry will not change (4xx): the page stops polling and says so. */
export const isClientError = (e: unknown): boolean => e instanceof OnboardingApiError && e.status >= 400 && e.status < 500;

export const errorCode = (e: unknown): string | null => (e instanceof OnboardingApiError ? e.code : null);

/** A `/v2/edit/...` contract path with its `{params}` filled, on the API origin. */
export function editUrl(endpointPath: string, pathParams: Record<string, string | number> = {}, apiUrl = config.apiUrl) {
    let path = endpointPath;
    Object.entries(pathParams).forEach(([name, value]) => {
        path = path.replace(`{${name}}`, encodeURIComponent(String(value)));
    });
    return `${apiUrl}${path.slice(EDIT_PREFIX.length)}`;
}

export function buildUrl(operation: OperationId, pathParams: Record<string, string | number> = {}, apiUrl = config.apiUrl) {
    return editUrl(ENDPOINTS[operation].path, pathParams, apiUrl);
}

export interface ClientOptions {
    getToken: () => string | null | undefined;
    onUnauthorized: () => void;
    apiUrl?: string;
}

interface CallOptions {
    path?: Record<string, string | number>;
    json?: any;
    form?: FormData;
    signal?: AbortSignal;
    blob?: boolean;
}

type EndpointMap = Record<string, { method: string; path: string }>;

/** The call of an own-tenant `/v2/edit/` client keyed by operationId (the Onboarding page's, What's next's):
 *  the token per call, a 401 signs out (noting a refused session's reason), any other refusal is an
 *  `OnboardingApiError` the page shows. */
export function createCaller<E extends EndpointMap>(endpoints: E, { getToken, onUnauthorized, apiUrl = config.apiUrl }: ClientOptions) {
    return async <T,>(operation: keyof E, { path = {}, json, form, signal, blob = false }: CallOptions = {}): Promise<T> => {
        const { method } = endpoints[operation];
        const headers: Record<string, string> = { Accept: blob ? '*/*' : 'application/json', Authorization: `Token ${getToken()}` };
        if (json !== undefined) {
            headers['Content-Type'] = 'application/json';
        }
        // A FormData body sets no Content-Type: the browser writes multipart with its boundary.
        let res: Response;
        try {
            res = await fetch(editUrl(endpoints[operation].path, path, apiUrl), {
                method,
                headers,
                cache: 'no-cache',
                body: form !== undefined ? form : json !== undefined ? JSON.stringify(json) : undefined,
                signal,
            });
        } catch (e: any) {
            if (e && e.name === 'AbortError') {
                throw e;
            }
            throw new OnboardingApiError(0, null);
        }
        const ok = res.status >= 200 && res.status < 300;
        if (ok && blob) {
            return (await res.blob()) as unknown as T;
        }
        let data: any = null;
        const ctype = res.headers.get('Content-Type') || '';
        if (res.status !== 204 && ctype.includes('json')) {
            try {
                data = await res.json();
            } catch (_e) {
                data = null;
            }
        }
        if (res.status === 401) {
            // A session the backend no longer accepts (two-step sign-in now required, or SSO only): the sign-in
            // page says why after the sign-out, as for the data provider's 401s (auth/signOutReason.js).
            noteSessionRefusal(data);
            onUnauthorized();
            throw new OnboardingApiError(401, data);
        }
        if (!ok) {
            const retry = Number(res.headers.get('Retry-After'));
            throw new OnboardingApiError(res.status, data, Number.isFinite(retry) && retry > 0 ? retry : null);
        }
        return data as T;
    };
}

/** A date-range import's first and last start date, as the pickers hold them. */
export interface BookingRange {
    from: Moment;
    to: Moment;
}

/** The selection's body: the range's calendar dates as `YYYY-MM-DD` (never `toISOString`, which would move a
 *  local midnight to the day before east of UTC); without a range exactly `{product_ids}`, the sample import. */
export function selectionBody(productIds: number[], range?: BookingRange | null): OnboardingSelectionRequest {
    if (!range) {
        return { product_ids: productIds };
    }
    return { product_ids: productIds, bookings_from: range.from.format('YYYY-MM-DD'), bookings_to: range.to.format('YYYY-MM-DD') };
}

export function createOnboardingApi(options: ClientOptions) {
    const call = createCaller(ENDPOINTS, options);

    return {
        state: (signal?: AbortSignal) => call<OnboardingState>('onboardingState', { signal }),
        logo: (signal?: AbortSignal) => call<OnboardingLogo>('onboardingLogo', { signal }),
        chooseLogo: (choice: OnboardingLogoChoice) => call<OnboardingLogo>('onboardingSetLogo', { json: choice }),
        uploadLogo: (file: File) => {
            const form = new FormData();
            form.append('file', file);
            return call<OnboardingLogo>('onboardingSetLogo', { form });
        },
        candidateImage: (index: number, signal?: AbortSignal) =>
            call<Blob>('onboardingLogoCandidateImage', { path: { index }, signal, blob: true }),
        discovery: (body: OnboardingDiscoveryRequest = {}) => call<OnboardingDiscoveryStarted>('onboardingDiscovery', { json: body }),
        /** Import the chosen tours with their sample bookings or, given a range, their bookings in it. */
        selection: (productIds: number[], range?: BookingRange | null) =>
            call<OnboardingSelectionResult>('onboardingSelection', { json: selectionBody(productIds, range) }),
        finish: () => call<OnboardingFinishResult>('onboardingFinish'),
        /** Ask our team to take the account live; a blank note is left out. Answers the go-live status. */
        requestGoLive: (note?: string | null) =>
            call<OnboardingGoLive>('onboardingRequestGoLive', { json: note && note.trim() ? { note: note.trim() } : {} }),
        withdrawGoLive: () => call<OnboardingGoLive>('onboardingWithdrawGoLiveRequest'),
        /** Whether the signed-in user's account is still being set up (the setup gate). */
        setupStatus: (signal?: AbortSignal) => call<OnboardingSetupStatus>('onboardingSetupStatus', { signal }),
    };
}

export type OnboardingApi = ReturnType<typeof createOnboardingApi>;

/** The onboarding API for the signed-in session. Identity-stable: the token is read per call (it
 *  rotates), and a 401 signs out and comes back to the page the user was on. */
export function useOnboardingApi(): OnboardingApi {
    const { token, logout } = React.useContext(ContextStore) as any;
    const tokenRef = React.useRef(token);
    tokenRef.current = token;
    const logoutRef = React.useRef(logout);
    logoutRef.current = logout;
    return React.useMemo(
        () => createOnboardingApi({
            getToken: () => tokenRef.current,
            onUnauthorized: () => logoutRef.current?.(window.location.pathname),
        }),
        [],
    );
}
